/**
 * AI视频创作平台 - 配置常量
 */

import { SCENE_CAMERA_MOVES, SCENE_SHOT_TYPES } from './scene-options';

/**
 * 视觉风格配置
 */
export const VISUAL_STYLES = [
  { value: 'realistic', label: '写实风格', description: '真实感强的画面风格' },
  { value: 'illustration', label: '插画风格', description: '手绘插画风格' },
  { value: 'anime', label: '动漫风格', description: '日系动漫风格' },
  { value: 'watercolor', label: '水彩风格', description: '水彩画风格' },
  { value: 'oil_painting', label: '油画风格', description: '油画艺术风格' },
  { value: '3d', label: '3D风格', description: '三维渲染风格' },
  { value: 'chinese_ancient', label: '古风', description: '中国古典风格' }
] as const;

/**
 * 视频尺寸比例配置
 */
export const VIDEO_RATIOS = [
  { value: '16:9', label: '横版 16:9', description: '适合电脑、电视播放' },
  { value: '9:16', label: '竖版 9:16', description: '适合手机、短视频平台' },
  { value: '1:1', label: '方形 1:1', description: '适合社交媒体' }
] as const;

/**
 * 景别配置
 */
export const SHOT_TYPES = SCENE_SHOT_TYPES.map(({ value, label, description, storageAliases }) => ({
  value: storageAliases?.[0] ?? value,
  label,
  description,
}));

/**
 * 运镜方式配置
 */
export const CAMERA_MOVEMENTS = SCENE_CAMERA_MOVES.map(({ value, label, description, storageAliases }) => ({
  value: storageAliases?.[0] ?? value,
  label: value === 'static' ? '固定' : label,
  description,
}));

/**
 * 视频分辨率配置
 */
export const VIDEO_RESOLUTIONS = [
  { value: '480p', label: '480P', description: '标清，文件较小' },
  { value: '720p', label: '720P', description: '高清，推荐选择' },
  { value: '1080p', label: '1080P', description: '全高清，画质最佳' }
] as const;

/**
 * 音色类型配置
 */
export const VOICE_TYPES = [
  { value: 'male', label: '男声' },
  { value: 'female', label: '女声' },
  { value: 'child', label: '童声' }
] as const;

/**
 * 形象分类配置
 */
export const AVATAR_CATEGORIES = [
  { value: 'male', label: '男性' },
  { value: 'female', label: '女性' },
  { value: 'child', label: '儿童' },
  { value: 'elderly', label: '老人' },
  { value: 'animal', label: '动物' },
  { value: 'other', label: '其他' }
] as const;

/**
 * 场景分类配置
 */
export const SCENE_CATEGORIES = [
  { value: 'indoor', label: '室内' },
  { value: 'outdoor', label: '室外' },
  { value: 'nature', label: '自然' },
  { value: 'city', label: '城市' },
  { value: 'fantasy', label: '幻想' },
  { value: 'other', label: '其他' }
] as const;

/**
 * 道具分类配置
 */
export const PROP_CATEGORIES = [
  { value: 'daily', label: '日常用品' },
  { value: 'vehicle', label: '交通工具' },
  { value: 'weapon', label: '武器装备' },
  { value: 'food', label: '食物饮品' },
  { value: 'plant', label: '植物花卉' },
  { value: 'tech', label: '科技设备' },
  { value: 'magic', label: '魔法道具' },
  { value: 'other', label: '其他' }
] as const;

/**
 * 故事状态配置
 */
export const STORY_STATUSES = [
  { value: 'draft', label: '草稿', color: 'default' },
  { value: 'in_progress', label: '进行中', color: 'processing' },
  { value: 'completed', label: '已完成', color: 'success' }
] as const;

/**
 * 创作类型配置
 */
export const CREATION_TYPES = [
  { value: 'story', label: '我有故事', icon: 'book', description: '输入故事文本，AI自动拆分镜' },
  { value: 'script', label: '我有脚本', icon: 'file-text', description: '上传分镜脚本，保留原有结构' },
  { value: 'novel', label: '我有小说', icon: 'read', description: '上传小说，AI智能改编（即将上线）', disabled: true }
] as const;

/**
 * 字幕字体配置
 * 前后端统一使用此配置，确保字体列表一致
 * 
 * 字体文件位置：
 * - 开发环境：使用系统字体（Windows）
 * - 生产环境：使用项目自带字体（server/fonts/）
 */
export const SUBTITLE_FONTS = [
  { 
    value: 'Microsoft YaHei', 
    label: '微软雅黑',
    fontPath: {
      // 系统路径（开发环境）
      system: {
        normal: '/Windows/Fonts/msyh.ttc',
        bold: '/Windows/Fonts/msyhbd.ttc',
      },
      // 项目路径（生产环境）
      bundled: {
        normal: '/fonts/NotoSansSC-Regular.ttf',  // 使用开源替代
        bold: '/fonts/NotoSansSC-Bold.ttf',
      }
    }
  },
  { 
    value: 'SimHei', 
    label: '黑体',
    fontPath: {
      system: {
        normal: '/Windows/Fonts/simhei.ttf',
        bold: '/Windows/Fonts/simhei.ttf',
      },
      bundled: {
        normal: '/fonts/NotoSansSC-Black.ttf',
        bold: '/fonts/NotoSansSC-Black.ttf',
      }
    }
  },
  { 
    value: 'SimSun', 
    label: '宋体',
    fontPath: {
      system: {
        normal: '/Windows/Fonts/simsun.ttc',
        bold: '/Windows/Fonts/simsunb.ttf',
      },
      bundled: {
        normal: '/fonts/NotoSerifSC-Regular.ttf',
        bold: '/fonts/NotoSerifSC-Bold.ttf',
      }
    }
  },
  { 
    value: 'KaiTi', 
    label: '楷体',
    fontPath: {
      system: {
        normal: '/Windows/Fonts/simkai.ttf',
        bold: '/Windows/Fonts/simkai.ttf',
      },
      bundled: {
        normal: '/fonts/LXGWWenKai-Regular.ttf',  // 霞鹜文楷（开源）
        bold: '/fonts/LXGWWenKai-Bold.ttf',
      }
    }
  },
  { 
    value: 'FangSong', 
    label: '仿宋',
    fontPath: {
      system: {
        normal: '/Windows/Fonts/simfang.ttf',
        bold: '/Windows/Fonts/simfang.ttf',
      },
      bundled: {
        normal: '/fonts/FangSong.ttf',
        bold: '/fonts/FangSong.ttf',
      }
    }
  },
  { 
    value: 'Microsoft JhengHei', 
    label: '微软正黑体',
    fontPath: {
      system: {
        normal: '/Windows/Fonts/msjh.ttc',
        bold: '/Windows/Fonts/msjhbd.ttc',
      },
      bundled: {
        normal: '/fonts/NotoSansTC-Regular.ttf',  // 繁体中文
        bold: '/fonts/NotoSansTC-Bold.ttf',
      }
    }
  },
  { 
    value: 'NSimSun', 
    label: '新宋体',
    fontPath: {
      system: {
        normal: '/Windows/Fonts/simsun.ttc',
        bold: '/Windows/Fonts/simsun.ttc',
      },
      bundled: {
        normal: '/fonts/NotoSerifSC-Light.ttf',
        bold: '/fonts/NotoSerifSC-Regular.ttf',
      }
    }
  },
  { 
    value: 'Arial', 
    label: 'Arial',
    fontPath: {
      system: {
        normal: '/Windows/Fonts/arial.ttf',
        bold: '/Windows/Fonts/arialbd.ttf',
      },
      bundled: {
        normal: '/fonts/Roboto-Regular.ttf',  // 开源替代
        bold: '/fonts/Roboto-Bold.ttf',
      }
    }
  },
  { 
    value: 'Times New Roman', 
    label: 'Times New Roman',
    fontPath: {
      system: {
        normal: '/Windows/Fonts/times.ttf',
        bold: '/Windows/Fonts/timesbd.ttf',
      },
      bundled: {
        normal: '/fonts/LibreBaskerville-Regular.ttf',
        bold: '/fonts/LibreBaskerville-Bold.ttf',
      }
    }
  },
  { 
    value: 'Courier New', 
    label: 'Courier New',
    fontPath: {
      system: {
        normal: '/Windows/Fonts/cour.ttf',
        bold: '/Windows/Fonts/courbd.ttf',
      },
      bundled: {
        normal: '/fonts/RobotoMono-Regular.ttf',
        bold: '/fonts/RobotoMono-Bold.ttf',
      }
    }
  },
  { 
    value: 'Verdana', 
    label: 'Verdana',
    fontPath: {
      system: {
        normal: '/Windows/Fonts/verdana.ttf',
        bold: '/Windows/Fonts/verdanab.ttf',
      },
      bundled: {
        normal: '/fonts/OpenSans-Regular.ttf',
        bold: '/fonts/OpenSans-Bold.ttf',
      }
    }
  },
  { 
    value: 'Georgia', 
    label: 'Georgia',
    fontPath: {
      system: {
        normal: '/Windows/Fonts/georgia.ttf',
        bold: '/Windows/Fonts/georgiab.ttf',
      },
      bundled: {
        normal: '/fonts/Merriweather-Regular.ttf',
        bold: '/fonts/Merriweather-Bold.ttf',
      }
    }
  },
] as const;

/**
 * 字幕字体类型（从配置中提取）
 */
export type SubtitleFontFamily = typeof SUBTITLE_FONTS[number]['value'];

/**
 * 火山引擎 Direct Edit API 支持的字体 ID 列表
 * 参考文档：https://www.volcengine.com/docs/4/102412#字体-id
 * 
 * 字体 ID 用于 text 轨道的 FontType 字段
 */
export const VOLC_FONT_IDS = [
  // 中文字体
  { id: '1525741', label: '思源黑体', description: '无衬线，适合正文' },
  { id: '1525742', label: '思源宋体', description: '衬线，适合正式场合' },
  { id: '1525743', label: '楷体', description: '楷书风格' },
  { id: '1525744', label: '黑体', description: '粗体无衬线' },
  { id: '1525745', label: '宋体', description: '传统宋体' },
  { id: '1525746', label: '仿宋', description: '仿宋体' },
  { id: '1525747', label: '圆体', description: '圆润无衬线' },
  { id: '1525748', label: '行楷', description: '行书楷体' },
  // 英文字体
  { id: '1525749', label: 'Arial', description: '通用无衬线' },
  { id: '1525750', label: 'Times New Roman', description: '衬线英文' },
  { id: '1525751', label: 'Courier New', description: '等宽字体' },
] as const;

/**
 * 火山引擎字体 ID 类型
 */
export type VolcFontId = typeof VOLC_FONT_IDS[number]['id'];
